import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useWindowSize } from '@react-hook/window-size';
import AppContext from '../../../Context/AppContext';
import { enablePortalV2Session, getPortalV2Paths, guardPortalV2Route } from '../../../util/portalV2';
import configData from '../../../config';
import QuestionaryStep1 from '../../Step1/QuestionaryStep1';
import SelectGoalsStep2 from '../../Step2/SelectGoalsStep2';
import EstablishActionStep3 from '../../Step3/EstablishActionStep3';
import Step5MidYearReview from '../../Step1/Step5MidYearReview';
import Step12 from '../../Step1/Step12/Step12';
import Step2Level2 from '../../Step2/Step2Level2';
import '../portalV2Shared.css';
import './StepsV2.css';
import './StepsV2Assessment.css';

const PHASE1_STEPS = [
    { id: 1, label: 'Where you are', hint: 'Assess' },
    { id: 2, label: 'Where you want to go', hint: 'Goals' },
    { id: 3, label: 'How you get there', hint: 'Actions' },
];

function stepFromSession(session) {
    const id = Number(session?.userStepId);
    if (Number.isFinite(id) && id >= 1 && id <= 7) return id;
    return 0;
}

/**
 * Portal V2 Steps orchestrator — parallel to /steps.
 * V2 chrome + stepper; Phase 1 content reuses proven step logic
 * (same APIs) while we continue restyling each step screen.
 */
const StepsV2 = () => {
    const { updateUserStepId, checkUserSession } = useContext(AppContext);
    const history = useHistory();
    const [, height] = useWindowSize();
    const session = checkUserSession() || {};
    const initialStep = stepFromSession(session);

    const [indexStep, setIndexStep] = useState(initialStep);
    const [headerMode, setHeaderMode] = useState(
        initialStep >= 1 && initialStep <= 3 ? 1 : initialStep >= 6 ? 2 : 1
    );
    const [userStatus, setUserStatus] = useState({});
    const [ready, setReady] = useState(false);
    const [bootError, setBootError] = useState('');
    const [step1Reveal, setStep1Reveal] = useState(false);
    const [mobile, setMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth <= 768 : false
    );

    useEffect(() => {
        const onResize = () => setMobile(window.innerWidth <= 768);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (!guardPortalV2Route(history, '/steps')) return undefined;
        enablePortalV2Session();
        let cancelled = false;

        const previewParam = (() => {
            try {
                return new URLSearchParams(window.location.search).get(
                    'preview'
                );
            } catch {
                return null;
            }
        })();
        const forceStep1Assessment =
            previewParam === 'fun-final' ||
            previewParam === 'fun' ||
            previewParam === 'fun-start' ||
            previewParam === 'fitness' ||
            previewParam === 'fitness-questions' ||
            previewParam === 'questions' ||
            previewParam === 'fitness-final' ||
            previewParam === 'section-complete' ||
            previewParam === 'step1-final' ||
            previewParam === 'step1-complete';
        const forceStep2Goals =
            previewParam === 'step2' ||
            previewParam === 'step2-goals' ||
            previewParam === 'goals';
        const forceStep2Pick7 =
            previewParam === 'step2-pick7' ||
            previewParam === 'pick7' ||
            previewParam === 'top7';
        const forceStep2Arrange =
            previewParam === 'step2-arrange' ||
            previewParam === 'arrange' ||
            previewParam === 'arrange7';
        const forceStep2OolaOne =
            previewParam === 'step2-oolaone' ||
            previewParam === 'oolaone' ||
            previewParam === 'oola1';
        const forceStep2Finish =
            previewParam === 'step2-finish' ||
            previewParam === 'step2-complete' ||
            previewParam === 'finish';
        const forceStep3Actions =
            previewParam === 'step3' ||
            previewParam === 'step3-actions' ||
            previewParam === 'actions';
        const forceStep3Tracker =
            previewParam === 'step3-tracker' ||
            previewParam === 'tracker' ||
            previewParam === 'step3-finish';

        (async () => {
            const paths = getPortalV2Paths();
            try {
                const userStepCheck = await updateUserStepId();
                if (cancelled) return;

                setUserStatus(userStepCheck || {});
                const userStepId = Number(
                    userStepCheck?.userStepId ??
                        checkUserSession()?.userStepId
                );
                const userInfo = checkUserSession() || {};

                if (userInfo.userTypeId === 4) {
                    history.replace(paths.nonSubscriber);
                    return;
                }

                // Dev preview: jump into Step 1 assessment shell regardless of session step
                if (forceStep1Assessment) {
                    setIndexStep(1);
                    setHeaderMode(1);
                    setReady(true);
                    return;
                }

                // Dev preview: Step 2 Goals workspace
                if (
                    forceStep2Goals ||
                    forceStep2Pick7 ||
                    forceStep2Arrange ||
                    forceStep2OolaOne ||
                    forceStep2Finish
                ) {
                    setIndexStep(2);
                    setHeaderMode(1);
                    setReady(true);
                    return;
                }

                if (forceStep3Actions || forceStep3Tracker) {
                    setIndexStep(3);
                    setHeaderMode(1);
                    setReady(true);
                    return;
                }

                if (userStepId >= 1 && userStepId <= 3) {
                    setIndexStep(userStepId);
                    setHeaderMode(1);
                    setReady(true);
                } else if (userStepId === 5) {
                    setIndexStep(5);
                    setHeaderMode(0);
                    setReady(true);
                } else if (userStepId === 6) {
                    setIndexStep(6);
                    setHeaderMode(2);
                    setReady(true);
                } else if (userStepId === 7) {
                    setIndexStep(7);
                    setHeaderMode(2);
                    setReady(true);
                } else if (userStepId === 0) {
                    history.replace(paths.customize);
                } else if (userStepId > 3) {
                    history.replace(paths.dashboard);
                } else {
                    setBootError('Could not determine your current step.');
                    setReady(true);
                }
            } catch (err) {
                if (cancelled) return;
                if (forceStep1Assessment) {
                    setIndexStep(1);
                    setHeaderMode(1);
                    setReady(true);
                    return;
                }
                if (
                    forceStep2Goals ||
                    forceStep2Pick7 ||
                    forceStep2Arrange ||
                    forceStep2OolaOne ||
                    forceStep2Finish
                ) {
                    setIndexStep(2);
                    setHeaderMode(1);
                    setReady(true);
                    return;
                }
                if (forceStep3Actions || forceStep3Tracker) {
                    setIndexStep(3);
                    setHeaderMode(1);
                    setReady(true);
                    return;
                }
                // Fall back to session step so the shell is never an empty void
                const fallback = stepFromSession(checkUserSession());
                if (fallback >= 1 && fallback <= 3) {
                    setIndexStep(fallback);
                    setHeaderMode(1);
                }
                setBootError(
                    'Taking longer than usual to sync your progress. Showing your last known step.'
                );
                setReady(true);
            }
        })();

        return () => {
            cancelled = true;
        };
        // Mount-only boot — avoid re-running when context fn identities change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const change = (i) => setIndexStep(i);
    const handleChangeDirect = () => {};
    const activePhase = PHASE1_STEPS.findIndex((s) => s.id === indexStep);
    // On Step 1 score reveal: mark Assess done and highlight Goals as next
    const displayPhase =
        step1Reveal && indexStep === 1 ? 1 : activePhase;

    return (
        <div className="pv2 pv2-steps">
            <div className="pv2-steps__stage">
                <header className="pv2-steps__top pv2-anim">
                    <div>
                        <p className="pv2-eyebrow">Design my life blueprint</p>
                        <h1 className="pv2-steps__title">
                            {headerMode === 2
                                ? 'Level 2 journey'
                                : indexStep === 5
                                  ? 'Mid-year review'
                                  : 'Your three steps'}
                        </h1>
                    </div>
                </header>

                {headerMode === 1 && indexStep >= 1 && indexStep <= 3 ? (
                    <div className="pv2-steps__stepper-wrap">
                        <nav
                            className="pv2-steps__stepper pv2-glass"
                            aria-label="Steps"
                        >
                            {PHASE1_STEPS.map((step, i) => {
                                const state =
                                    i < displayPhase
                                        ? 'is-done'
                                        : i === displayPhase
                                          ? 'is-active'
                                          : '';
                                return (
                                    <div
                                        key={step.id}
                                        className={`pv2-steps__step ${state}${
                                            step1Reveal &&
                                            indexStep === 1 &&
                                            i === 1
                                                ? ' is-next'
                                                : ''
                                        }`}
                                        aria-label={`Step ${step.id}: ${step.label}${
                                            step.hint ? ` — ${step.hint}` : ''
                                        }`}
                                        aria-current={
                                            i === displayPhase
                                                ? 'step'
                                                : undefined
                                        }
                                    >
                                        <span
                                            className="pv2-steps__num"
                                            aria-hidden
                                        >
                                            {i < displayPhase
                                                ? '✓'
                                                : `0${step.id}`}
                                        </span>
                                        <div>
                                            <strong>{step.label}</strong>
                                            <em>{step.hint}</em>
                                        </div>
                                    </div>
                                );
                            })}
                        </nav>
                        <p className="pv2-steps__step-caption" aria-hidden="true">
                            Step {PHASE1_STEPS[displayPhase]?.id || indexStep} ·{' '}
                            {PHASE1_STEPS[displayPhase]?.hint || 'Goals'}
                        </p>
                    </div>
                ) : null}

                <div className="pv2-steps__body">
                    {!ready ? (
                        <div className="pv2-steps__loading" aria-live="polite">
                            <div className="pv2-steps__loading-pulse" />
                            <p>Opening Step {indexStep || 1}…</p>
                            <span>Syncing your blueprint</span>
                        </div>
                    ) : null}

                    {ready && bootError ? (
                        <p className="pv2-steps__boot-hint">{bootError}</p>
                    ) : null}

                    {ready && indexStep === 1 ? (
                        <QuestionaryStep1
                            indexStepChange={change}
                            mobile={mobile}
                            handleChangeHeader={setHeaderMode}
                            userStatus={userStatus}
                            headerActive={headerMode === 1 || headerMode === true}
                            portalV2
                            onStep1Reveal={setStep1Reveal}
                        />
                    ) : null}
                    {ready && indexStep === 2 ? (
                        <SelectGoalsStep2
                            handleChangeindexStep={setIndexStep}
                            handleChangeHeader={setHeaderMode}
                            mobile={mobile}
                            portalV2
                        />
                    ) : null}
                    {ready && indexStep === 3 ? (
                        <EstablishActionStep3
                            handleChangeHeader={setHeaderMode}
                            mobile={mobile}
                            portalV2
                        />
                    ) : null}
                    {ready && indexStep === 5 ? (
                        <Step5MidYearReview
                            indexStepChange={change}
                            mobile={mobile}
                            handleChangeHeader={setHeaderMode}
                            userStatus={userStatus}
                        />
                    ) : null}
                    {ready && indexStep === 6 ? (
                        <Step12
                            changeStepIndex={change}
                            configData={configData}
                            mobile={mobile}
                            height={height}
                            handleChangeHeader={setHeaderMode}
                            userStatus={userStatus}
                            handleChange={handleChangeDirect}
                            changeIndexLevelStep={() => {}}
                        />
                    ) : null}
                    {ready && indexStep === 7 ? (
                        <Step2Level2
                            handleChangeindexStep={setIndexStep}
                            handleChangeHeader={setHeaderMode}
                            mobile={mobile}
                        />
                    ) : null}

                    {ready && (indexStep < 1 || indexStep > 7) ? (
                        <div className="pv2-steps__loading">
                            <p>No step content to show.</p>
                            <span>Try returning to the dashboard.</span>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default StepsV2;
